import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';

/**
 * Path-segment driven breadcrumbs for the new MyPages admin shell.
 *
 * The app uses `<BrowserRouter>` (not `createBrowserRouter`), so the data-router
 * `useMatches()` hook is not available. Instead we walk the URL segments and
 * resolve them against a static label map. Unknown ids (numbers) are rendered
 * as "#<id>".
 */

const SEGMENT_LABELS = {
  admin: 'Admin',
  salesperson: 'Säljare',
  produktion: 'Produktion',

  crm: 'CRM',
  leverans: 'Leverans',
  drift: 'Drift & admin',

  pipeline: 'Pipeline',
  abonnemang: 'Abonnemang',
  faktureringsbeslut: 'Faktureringsbeslut',
  saljardashboard: 'Säljardashboard',
  arr: 'ARR & budget',
  retention: 'Retention',
  aktivitet: 'Aktivitetsöversikt',
  budgetar: 'Budgetar',
  integrationer: 'Integrationer',

  kundlista: 'Kundlista',
  kundföretag: 'Kundföretag',
  'bolags-kpi': 'Bolagsöversikt KPI',
  produktionsvy: 'Produktionsvy',
  fakturor: 'Fakturaöversikt',
  forsaljningsanalys: 'Försäljningsanalys',
  'mrr-analys': 'MRR-analys',
  leadsoversikt: 'Leadsöversikt',
  'arr-budget': 'ARR mot budget',

  anvandare: 'Användare',
  produkter: 'Produktregister',
  utm: 'UTM-spårning',
  'fortnox-status': 'Fortnox-status',
  'svea-status': 'Svea-status',
  notifikationer: 'Notifikationer',
  notifikationsinstallningar: 'Notifikationsinställningar',

  nytt: 'Ny',
  redigera: 'Redigera',
};

const HIDDEN_ROOT_SEGMENTS = new Set(['admin', 'salesperson', 'produktion', 'client', 'user', 'partner']);

const labelFor = (segment) => {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  if (/^\d+$/.test(segment)) return `#${segment}`;
  // Fall back to a humanised version of the slug
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
};

// location.pathname keeps segments percent-encoded, so slugs with Swedish
// characters (e.g. "kundföretag" -> "kundf%C3%B6retag") must be decoded
// before label lookup.
const decodeSegment = (segment) => {
  try {
    return decodeURIComponent(segment);
  } catch (err) {
    return segment;
  }
};

const AdminBreadcrumbs = () => {
  const { pathname } = useLocation();
  const parts = pathname.split('/').filter(Boolean);

  if (parts.length === 0) return null;

  // Hide breadcrumbs entirely on the role landing pages (/admin, /salesperson, /produktion).
  if (parts.length === 1 && HIDDEN_ROOT_SEGMENTS.has(parts[0])) return null;

  const crumbs = parts.map((segment, idx) => {
    const href = `/${parts.slice(0, idx + 1).join('/')}`;
    return { href, label: labelFor(decodeSegment(segment)) };
  });

  return (
    <Box sx={{ px: 4, pt: 2 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;
          if (isLast) {
            return (
              <Typography key={crumb.href} color="text.primary">
                {crumb.label}
              </Typography>
            );
          }
          return (
            <Link
              key={crumb.href}
              component={RouterLink}
              to={crumb.href}
              underline="hover"
              color="inherit"
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default AdminBreadcrumbs;
